import React, { useState, useEffect } from 'react';
import StepScreenshot from './StepScreenshot';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';

const BrowserStepForm = ({
  formData,
  setFormData,
  handleSaveStep,
  handleToggleSecret,
  handleValueModeChange,
  handleGenerateAssertion,
  isAssertionGenerating,
  genratedAssertions,
  TEST_ACTIONS,
  availableWorkflows,
  handleKeyPress,
  error,
  stepScreenshot,
  setStepScreenshot,
  screenshotPreview,
  setScreenshotPreview,
  isUploadingScreenshot,
  setIsUploadingScreenshot,
  existingScreenshotUrl,
  onRemoveExistingScreenshot 
}) => {
  const [selectedRefStepId, setSelectedRefStepId] = useState('');
  const [assertionMode, setAssertionMode] = useState(() => {
    if (formData.currentStep && formData.currentStep.action === 'assert') {
      const isNewAssert = !formData.currentStep.value && formData.editingStepIndex === undefined && !formData.currentStep.assertion_mode;
      return isNewAssert ? 'strict' : (formData.currentStep.assertion_mode || 'flexible');
    }
    return 'flexible';
  });

  const ASSERT_INPUT_ID = 'assert-text-input';

  useEffect(() => {
    if (formData.currentStep && formData.currentStep.action === 'assert') {
      const isNewAssert = !formData.currentStep.value && formData.editingStepIndex === undefined && !formData.currentStep.assertion_mode;
      const mode = isNewAssert ? 'strict' : (formData.currentStep.assertion_mode || 'flexible');
      setAssertionMode(mode);
      if (isNewAssert) {
        // Persist default mode into the current step so it is tracked downstream
        setFormData(prev => ({
          ...prev,
          currentStep: { ...prev.currentStep, assertion_mode: 'strict' }
        }));
      }
    }
  }, [formData.currentStep.action, formData.currentStep.assertion_mode, formData.currentStep.value, formData.editingStepIndex, setFormData]);

  const formatStepDescription = (step) => {
    if (!step) return '';
    const action = step.action;
    if (action === 'type') return `Type "${step.secret ? '••••••••' : (step.value || '')}" into ${step.element || ''}`;
    if (action === 'click') return `Click ${step.element || ''}`;
    if (action === 'navigate') return `Navigate to ${step.value || ''}`;
    if (action === 'select') return `Select "${step.value || ''}" in ${step.element || ''}`;
    if (action === 'wait') return `Wait ${step.value || ''} ms`;
    if (action === 'reload') return 'Reload page';
    if (action === 'workflow') return `Run workflow`;
    return action;
  };

  // Resolve tokens like {{step:<id>.value}} to the actual step value for preview display only
  const resolveStepReferences = (text) => {
    if (!text) return '';
    return text.replace(/\{\{\s*step:([^}]+?)\.value\s*\}\}/g, (match, stepId) => {
      const step = (formData.savedSteps || []).find(s => s.id === stepId);
      if (!step) return match;
      return step.value !== undefined && step.value !== null ? String(step.value) : '';
    });
  };

  const insertStepReferenceAtCursor = (step) => {
    if (!step) return;
    const input = document.getElementById(ASSERT_INPUT_ID);
    const currentText = formData.currentStep.value || '';
    const cursorPosition = input && typeof input.selectionStart === 'number' ? input.selectionStart : currentText.length;
    const before = currentText.substring(0, cursorPosition);
    const after = currentText.substring(cursorPosition);
    const reference = `{{step:${step.id}.value}}`;
    const newText = `${before}${reference}${after}`;
    setFormData(prev => ({
      ...prev,
      currentStep: { ...prev.currentStep, value: newText }
    }));
    setTimeout(() => {
      if (input) {
        input.focus();
        const pos = cursorPosition + reference.length;
        input.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleStepSelect = (stepId) => {
    setSelectedRefStepId(stepId);
    const step = (formData.savedSteps || []).find(s => s.id === stepId);
    if (step) {
      insertStepReferenceAtCursor(step);
      setSelectedRefStepId('');
    }
  };

  return (
    <>
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Action
        </label>
        <select
          value={formData.currentStep.action}
          onChange={(e) => {
            const newAction = e.target.value;
            setFormData(prev => ({
              ...prev,
              currentStep: { 
                ...prev.currentStep, 
                action: newAction,
                exact: newAction === 'click' ? prev.currentStep.exact : false,
                secret: newAction === 'type' ? prev.currentStep.secret : false
              }
            }));
          }}
          className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
        >
          {TEST_ACTIONS.map(action => (
            <option key={action.value} value={action.value}>
              {action.label}
            </option>
          ))}
        </select>
      </div>

      {/* Workflow selection */}
      {formData.currentStep.action === 'workflow' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Workflow
          </label>
          <select
            value={formData.currentStep.workflowId || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              currentStep: { ...prev.currentStep, workflowId: e.target.value }
            }))}
            className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none mb-4"
          >
            <option value="">-- Select a workflow --</option>
            {availableWorkflows.map(workflow => (
              <option key={workflow.id} value={workflow.id}>
                {workflow.name}
              </option>
            ))}
          </select>
         
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Repeat Count
          </label>
          <div className="flex items-center">
            <input
              type="number"
              min="1"
              value={formData.currentStep.repeatCount || 1}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                setFormData(prev => ({
                  ...prev,
                  currentStep: { ...prev.currentStep, repeatCount: value }
                }));
              }}
              className="p-2 rounded border border-gray-300 focus:border-black focus:outline-none w-24"
            />
            <span className="ml-2 text-sm text-gray-600">time{formData.currentStep.repeatCount !== 1 ? 's' : ''}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            The workflow steps will be executed {formData.currentStep.repeatCount} time{formData.currentStep.repeatCount !== 1 ? 's' : ''} sequentially
          </p>
        </div>
      )}

      {/* Action fields */}
      {formData.currentStep.action !== 'workflow' && TEST_ACTIONS.find(action => action.value === formData.currentStep.action)?.fields.map(field => (
        <div key={field.name} className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {field.label}
          </label>
          <div className="flex">
            {formData.currentStep.action === 'assert' && field.name === 'value' ? (
              <div className="w-full space-y-3">
                {/* Assertion mode tabs */}
                <div className="flex rounded overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setAssertionMode('strict');
                      setFormData(prev => ({
                        ...prev,
                        currentStep: { ...prev.currentStep, assertion_mode: 'strict' }
                      }));
                    }}
                    className={`${assertionMode === 'strict' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'} flex-1 px-4 py-2 text-sm`}
                  >
                    Strict assertion
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAssertionMode('flexible');
                      setFormData(prev => ({
                        ...prev,
                        currentStep: { ...prev.currentStep, assertion_mode: 'flexible' }
                      }));
                    }}
                    className={`${assertionMode === 'flexible' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'} flex-1 px-4 py-2 text-sm`}
                  >
                    Flexible assertion
                  </button>
                </div>

                {assertionMode === 'flexible' ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor={ASSERT_INPUT_ID}>Assertion</Label>
                      <textarea
                        id={ASSERT_INPUT_ID}
                        placeholder="Enter assertion text..."
                        value={formData.currentStep.value || ''}
                        onChange={(e) => {
                          const text = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            currentStep: { ...prev.currentStep, value: text }
                          }));
                        }}
                        onKeyPress={handleKeyPress}
                        className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black h-28"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleGenerateAssertion}
                      disabled={isAssertionGenerating}
                      className="mt-2 bg-black hover:bg-gray-800 text-white font-medium py-2 px-4 rounded transition-colors"
                    >
                      {isAssertionGenerating ? "Generating..." : "Generate Assertion"}
                    </button>

                    {genratedAssertions && genratedAssertions.length > 0 && (
                      <div className="mt-2 space-y-2">
                        <label className="block text-xs font-medium text-gray-500">
                          Suggested Assertions
                        </label>
                        {genratedAssertions.map((assertion, index) => (
                          <button 
                            key={index} 
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                currentStep: { ...prev.currentStep, value: assertion }
                              }));
                            }}
                            className="block w-full text-left bg-gray-100 p-2 rounded hover:bg-gray-200 text-sm"
                          >
                            {assertion}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor={ASSERT_INPUT_ID}>Text to verify</Label>
                      <input
                        id={ASSERT_INPUT_ID}
                        type="text"
                        placeholder="Enter text that should be visible on the page..."
                        value={formData.currentStep.value || ''}
                        onChange={(e) => {
                          const text = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            currentStep: { ...prev.currentStep, value: text }
                          }));
                        }}
                        onKeyPress={handleKeyPress}
                        className="h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>

                    {(formData.savedSteps && formData.savedSteps.length > 0) && (
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-500">Use value from previous step:</Label>
                        <div className="flex gap-2">
                          <Select value={selectedRefStepId} onValueChange={handleStepSelect}>
                            <SelectTrigger className="flex-1 h-10">
                              <SelectValue placeholder="Choose a step to insert its value..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(formData.savedSteps || []).map((s, idx) => (
                                s.id && (s.value !== undefined && s.value !== '') ? (
                                  <SelectItem key={s.id} value={s.id}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Step {s.order || idx + 1}</span>
                                      <span className="truncate max-w-[260px]">{formatStepDescription(s)}</span>
                                    </div>
                                  </SelectItem>
                                ) : null
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="secondary"
                            onClick={() => selectedRefStepId && handleStepSelect(selectedRefStepId)}
                            disabled={!selectedRefStepId}
                            className="h-10 px-3"
                            type="button"
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    )}

                    {formData.currentStep.value && (
                      <div className="p-3 bg-gray-100 rounded-md">
                        <p className="text-sm text-gray-500 mb-1">Preview:</p>
                        <p className="font-mono text-sm">Text "{resolveStepReferences(formData.currentStep.value)}" should be visible</p>
                        {/* <p className="text-xs text-gray-500 mt-1">{formData.currentStep}</p> */}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <input
                type={formData.currentStep.secret && field.name === 'value' ? "password" : 
                      (formData.currentStep.action === 'wait' && field.name === 'value' ? "number" : 
                       (field.type || "text"))}
                value={formData.currentStep[field.name]}
                onChange={(e) => {
                  const newValue = e.target.value;
                  
                  if (formData.currentStep.action === 'click' && field.name === 'element') {
                    const hasExactBrackets = newValue && /^\{\{.*\}\}$/.test(newValue.trim());
                    setFormData(prev => ({
                      ...prev,
                      currentStep: { 
                        ...prev.currentStep, 
                        [field.name]: newValue,
                        exact: hasExactBrackets
                      }
                    }));
                  } else if (formData.currentStep.action === 'navigate' && field.name === 'value') {
                    const isFixtureFormat = newValue && /^\{[^{}]+\}$/.test(newValue.trim());
                    setFormData(prev => ({
                      ...prev,
                      currentStep: { 
                        ...prev.currentStep, 
                        [field.name]: newValue,
                        value_mode: isFixtureFormat ? 'fixture' : prev.currentStep.value_mode
                      }
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      currentStep: { ...prev.currentStep, [field.name]: newValue }
                    }));
                  }
                }}
                onKeyPress={handleKeyPress}
                placeholder={field.placeholder}
                className={`${formData.currentStep.action === 'click' && field.name === 'element' ? 'rounded-l' : (formData.currentStep.action === 'type' && field.name === 'value' ? 'rounded-l' : 'rounded')} w-full p-2 border border-gray-300 focus:border-black focus:outline-none`}
              />
            )}

            {formData.currentStep.action === 'type' && field.name === 'value' && (
              <button
                type="button"
                onClick={handleToggleSecret}
                className={`${
                  formData.currentStep.secret
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-gray-400 hover:bg-gray-500"
                } text-white px-3 py-2 rounded-r border-l-0 transition-colors flex items-center`}
                title={formData.currentStep.secret ? "Password field (value masked)" : "Regular text field"}
              >
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {formData.currentStep.secret ? 'Password ON' : 'Password OFF'}
              </button>
            )}
          </div>

          {/* Field-specific messages and buttons */}
          {formData.currentStep.action === 'type' && field.name === 'element' && formData.currentStep.element?.toLowerCase().includes('password') && !formData.currentStep.secret && (
            <div className="mt-1 text-xs text-amber-600 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Element name contains "password". Consider enabling password masking.
            </div>
          )}

          {/* Old generate/suggested assertion UI removed in favor of new builder */}
        </div>
      ))}

      {/* Step Screenshot for type, click, and select actions */}
      {(formData.currentStep.action === "type" || formData.currentStep.action === "click" || formData.currentStep.action === "select") && (
        <StepScreenshot
          stepScreenshot={stepScreenshot}
          setStepScreenshot={setStepScreenshot}
          screenshotPreview={screenshotPreview}
          setScreenshotPreview={setScreenshotPreview}
          isUploadingScreenshot={isUploadingScreenshot}
          setIsUploadingScreenshot={setIsUploadingScreenshot}
          stepId={formData.editingStepIndex !== undefined ? 
            formData.savedSteps[formData.editingStepIndex]?.id : 
            null
          }
          existingScreenshotUrl={existingScreenshotUrl}
          onRemoveExistingScreenshot={onRemoveExistingScreenshot}
        />
      )}

      {/* Value mode selection for type and navigate actions */}
      {(formData.currentStep.action === "type" || formData.currentStep.action === "navigate") && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Value Mode
          </label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => handleValueModeChange("static")}
              className={`px-3 py-2 rounded text-sm ${
                formData.currentStep.value_mode === "static"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800"
              }`}
            >
              Static
            </button>
            <button
              type="button"
              onClick={() => handleValueModeChange("fixture")}
              className={`px-3 py-2 rounded text-sm ${
                formData.currentStep.value_mode === "fixture"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-800"
              }`}
            >
              Fixture
            </button>
          </div>
          {formData.currentStep.value_mode === "fixture" && (
            <div className="mt-1 text-xs text-blue-600">
              {formData.currentStep.action === "navigate" 
                ? "The URL value will be treated as a fixture name. Use format: {fixture_name}"
                : "The value will be treated as a fixture name."}
            </div>
          )}
        </div>
      )}

      {/* Save and Cancel buttons */}
      <button
        type="button"
        onClick={handleSaveStep}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors mb-2"
      >
        {formData.editingStepIndex !== undefined ? 'Update Step' : 'Save Step'}
      </button>
      
      {formData.editingStepIndex !== undefined && (
        <button
          type="button"
          onClick={() => {
            setFormData(prev => ({
              ...prev,
              currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 },
              editingStepIndex: undefined
            }));
          }}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded transition-colors"
        >
          Cancel Edit
        </button>
      )}
    </>
  );
};

export default BrowserStepForm; 